import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@mui/material'
import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BankDetailsForm } from './bank-details-form'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Delete, Edit } from '@mui/icons-material'
import { BankDetails } from 'types/bankDetails'
import ConfirmationDialog from 'components/ConfirmationDialog'
import {
  useBankAccountsQuery,
  useDeleteBankAccountMutation,
} from 'services/bankAccounts'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'
import { toast } from 'react-toastify'

interface Props {
  organizationId?: string | null
}

export const BankInfo: FunctionComponent<Props> = ({ organizationId }) => {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useBankAccountsQuery()
  const [bankDetailsFormOpen, setBankDetailsFormOpen] = useState(false)
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)
  const [currentlyEditing, setCurrentlyEditing] = useState<BankDetails | null>(null)
  const [currentlyDeletingId, setCurrentlyDeletingId] = useState<string | null>()
  const [
    deleteBankAccount,
    {
      isLoading: isDeleteLoading,
      isSuccess: isDeleteSuccess,
      isError: isDeleteError,
    },
  ] = useDeleteBankAccountMutation()

  const handleAdd = () => {
    setBankDetailsFormOpen((prevState) => !prevState)
    setCurrentlyEditing(null)
  }

  const handleDelete = () => {
    if (currentlyDeletingId) {
      deleteBankAccount({ id: currentlyDeletingId })
    }
    setCurrentlyDeletingId(null)
  }

  useEffect(() => {
    if (isDeleteSuccess) {
      toast.success(t('operation-success'))
    }

    if (isDeleteError) {
      toast.success(t('operation-failure'))
    }
  }, [isDeleteError, isDeleteSuccess, t])

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontWeight="bold" variant="h6">
          {t('bank-info')}
        </Typography>
        <Button
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          onClick={handleAdd}
        >
          {t('add-bank-details')}
        </Button>
      </Box>

      <Box mt={2}>
        {isLoading && <Loading fullPage={false} />}
        {isError && <Error fullPage={false} />}
        {data &&
          data.map((bankAccount, index) => (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  pr={2}
                >
                  <Typography>
                    Bank hesabı #{bankAccount.id} : {bankAccount.name}
                  </Typography>
                  <Box display="flex" gap={1}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation()
                        setCurrentlyDeletingId(bankAccount.id)
                        setConfirmationDialogOpen(true)
                      }}
                    >
                      <Delete color="error" />
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation()
                        setCurrentlyEditing(bankAccount)
                        setBankDetailsFormOpen(true)
                      }}
                    >
                      <Edit color="primary" />
                    </IconButton>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <Grid container>
                    <Grid item xs={12} md={6}>
                      <ListItem sx={{ gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {t('bankName')}:
                        </Typography>
                        <Typography> {bankAccount.name}</Typography>
                      </ListItem>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ListItem sx={{ gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {t('accountCurrency')}:
                        </Typography>
                        <Typography>{bankAccount.accountCurrency}</Typography>
                      </ListItem>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ListItem sx={{ gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {t('accountNumber')}:
                        </Typography>
                        <Typography> {bankAccount.accountNumber}</Typography>
                      </ListItem>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ListItem sx={{ gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          SWIFT:
                        </Typography>
                        <Typography>
                          {bankAccount.correspondentSwiftBicCode}
                        </Typography>
                      </ListItem>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ListItem sx={{ gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {t('description')}:
                        </Typography>
                        <Typography>{bankAccount.description}</Typography>
                      </ListItem>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ListItem sx={{ gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {t('accountHolderName')}:
                        </Typography>
                        <Typography>{bankAccount.accountHolderName}</Typography>
                      </ListItem>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ListItem sx={{ gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {t('tin')}:
                        </Typography>
                        <Typography>{bankAccount.taxId}</Typography>
                      </ListItem>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ListItem sx={{ gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {t('beneficiaryBankName')}:
                        </Typography>
                        <Typography>{bankAccount.beneficiaryBankName}</Typography>
                      </ListItem>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ListItem sx={{ gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {t('beneficiaryBankAddress')}:
                        </Typography>
                        <Typography>{bankAccount.beneficiaryBankAddress}</Typography>
                      </ListItem>
                    </Grid>
                  </Grid>
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
      </Box>

      {bankDetailsFormOpen && (
        <BankDetailsForm
          handleClose={handleAdd}
          data={currentlyEditing}
          organizationId={organizationId ?? ''}
        />
      )}

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleDelete}
        text={t('are-you-sure-you-want-to-delete')}
      />
    </Box>
  )
}
